import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewAccessorQuestionGuidance from '@app/src/components/Drawer/Views/DrawerViewAccessorQuestionGuidance'
import EmptyState from '@app/src/components/EmptyState'
import { EmptyStateVariant } from '@app/src/components/EmptyState/EmptyState'
import Select from '@app/src/components/Form/Select/ControlledSelect'
import ControlledTextFieldWithFormatting from '@app/src/components/TextFieldWithFormatting/ControlledTextFieldWithFormatting'
import TextField from '@app/src/components/Ui/TextField'
import { useDialogState } from '@app/src/hooks/mui-hooks'
import AnswerClassificationPreviewCard from '@app/src/pages/QuestionEditor/AnswerClassification/AnswerClassificationPreviewCard'
import type { AssessmentQuestion, AssessmentSection, AssessmentTemplate } from '@app/src/types/resourceExplorer'
import { AssessmentLevelNamesType, AssessmentType } from '@app/src/types/resourceExplorer'
import { insertIf } from '@app/src/utils/helpersTs'
import { br } from '@app/src/utils/translationMarkup'
import { Add, Edit, InfoOutlined, ListAltOutlined } from '@mui/icons-material'
import type { RadioGroupProps } from '@mui/material'
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import type { FieldError } from 'react-hook-form'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import type { AssessmentTemplateFormValues } from './CreateAssessmentFromQuestionnaire'
import { EditQuestionClassificationDialog } from './EditQuestionClassificationDialog'
import { useAssessmentForm } from './hooks/useAssessmentForm'
import { ASSESSMENT_INTERVALS } from './utils/constants'
import type { EnhancedAssessmentFormQuestion } from './utils/types'

type AssessmentFormProps = {
  intervals: number
  sections: AssessmentSection[]
  isPublished?: boolean
  onIntervalsChange: RadioGroupProps['onChange']
  disablePublishButton?: boolean
  disableAssessmentTypeChange?: boolean
  questionnaireTemplateTitle?: string
  assessmentTemplate?: AssessmentTemplate
}

const AssessmentForm: React.FC<AssessmentFormProps> = ({
  intervals,
  sections,
  isPublished = false,
  onIntervalsChange,
  disablePublishButton = false,
  disableAssessmentTypeChange = false,
  questionnaireTemplateTitle,
  assessmentTemplate,
}) => {
  const { formatMessage } = useIntl()
  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = useFormContext<AssessmentTemplateFormValues>()
  const [isOpen, openDialog, closeDialog] = useDialogState()
  const [currentQuestion, setCurrentQuestion] = useState<EnhancedAssessmentFormQuestion | undefined>()
  const { openDrawer } = useDrawer()
  const isBaseline = assessmentTemplate?.assessmentType === AssessmentType.BaselineAssessment

  const {
    assessmentType,
    levelNamesType,
    createEnhancedQuestion,
    handleClassificationSave,
    initializeAssessmentFormatting,
    getFormPointsForQuestion,
  } = useAssessmentForm({ sections })

  useEffect(() => {
    initializeAssessmentFormatting()
  }, [initializeAssessmentFormatting])

  useEffect(() => {
    if (intervals === ASSESSMENT_INTERVALS.MAX && levelNamesType === AssessmentLevelNamesType.MitigationIntervals) {
      setValue('levelNamesType', AssessmentLevelNamesType.Intervals)
    }
  }, [intervals, levelNamesType, setValue])

  const openQuestionClassificationDialog = (question: AssessmentQuestion) => {
    const enhancedQuestion = createEnhancedQuestion(question)
    setCurrentQuestion(enhancedQuestion)
    openDialog()
  }

  return (
    <Stack alignItems="center">
      <Stack component="form" sx={{ maxWidth: { md: '70%', sm: '100%' }, mr: { md: 0, sm: 3 } }} spacing={4}>
        <Controller name="sections" control={control} defaultValue={[]} render={() => <input type="hidden" />} />

        <Stack spacing={3}>
          <Select
            control={control}
            required
            rules={{ required: true, message: formatMessage({ id: 'assessments.assessmentTypeRequired' }) }}
            name="assessmentType"
            fieldLabel={formatMessage({ id: 'assessments.assessmentType' })}
            defaultValue={AssessmentType.Flexible}
            disabled={disableAssessmentTypeChange || isBaseline}
            options={[
              {
                label: formatMessage({ id: 'assessments.standardAssessment' }),
                value: AssessmentType.StandardAssessment,
              },
              { label: formatMessage({ id: 'assessments.flexibleAssessment' }), value: AssessmentType.Flexible },
              ...insertIf(isBaseline, {
                label: formatMessage({ id: 'assessments.filter.baselineAssessment' }),
                value: AssessmentType.BaselineAssessment,
              }),
            ]}
          />
          <TextField
            name="name"
            inputRef={register({ required: formatMessage({ id: 'form.validation.required' }) })}
            label={formatMessage({ id: 'assessments.assessmentName' })}
            defaultValue={questionnaireTemplateTitle}
            error={!!errors.name}
            helperText={errors.name?.message}
            disabled={isBaseline}
          />
          <ControlledTextFieldWithFormatting
            name="description"
            control={control}
            multiline
            defaultValue={assessmentTemplate?.description}
            label={formatMessage({ id: 'assessments.assessmentDescription' })}
            disabled={isBaseline}
          />
        </Stack>

        <Tooltip
          title={formatMessage({ id: 'assessments.unpublishToolTip' }, { br })}
          placement="bottom-start"
          disableHoverListener={!disablePublishButton && !isBaseline}
        >
          <FormControlLabel
            control={<Switch color="primary" name="isPublished" defaultChecked={isPublished} inputRef={register()} />}
            labelPlacement="end"
            label={
              <FormLabel component="legend">{formatMessage({ id: 'assessments.availableForCustomers' })}</FormLabel>
            }
            sx={{ mb: 1 }}
            disabled={disablePublishButton || isBaseline}
          />
        </Tooltip>

        <Stack
          spacing={4}
          sx={{ borderRadius: 2, borderStyle: 'solid', borderWidth: 1, borderColor: 'grey.400', p: 4 }}
        >
          <Stack>
            <Typography variant="subtitle1" id="intervals-label">
              {formatMessage({ id: 'assessments.intervals' })}
            </Typography>
            <FormControl disabled={isBaseline}>
              <RadioGroup aria-labelledby="intervals-label" value={intervals} onChange={onIntervalsChange}>
                <Stack direction="row">
                  <FormControlLabel
                    value={ASSESSMENT_INTERVALS.MIN}
                    control={<Radio />}
                    label={formatMessage({ id: 'assessments.intervalsCount' }, { count: ASSESSMENT_INTERVALS.MIN })}
                  />
                  <FormControlLabel
                    value={ASSESSMENT_INTERVALS.MAX}
                    control={<Radio />}
                    label={formatMessage({ id: 'assessments.intervalsCount' }, { count: ASSESSMENT_INTERVALS.MAX })}
                  />
                </Stack>
              </RadioGroup>
            </FormControl>
          </Stack>

          {assessmentType === AssessmentType.Flexible && (
            <Select
              control={control}
              name="levelNamesType"
              fieldLabel={formatMessage({ id: 'assessments.levelNamesType' })}
              defaultValue={AssessmentLevelNamesType.Levels}
              disabled={isBaseline}
              options={[
                {
                  label: formatMessage({ id: `assessments.levelNamesTypes.levels.${intervals}` }),
                  value: AssessmentLevelNamesType.Levels,
                },
                {
                  label: formatMessage({ id: `assessments.levelNamesTypes.intervals.${intervals}` }),
                  value: AssessmentLevelNamesType.Intervals,
                },
                ...insertIf(intervals === ASSESSMENT_INTERVALS.MIN, {
                  label: formatMessage({ id: `assessments.levelNamesTypes.mitigationIntervals.${intervals}` }),
                  value: AssessmentLevelNamesType.MitigationIntervals,
                }),
              ]}
            />
          )}

          {!isBaseline &&
            Array.from({ length: intervals }).map((_, index) => (
              <Stack key={index}>
                <Typography variant="subtitle1">
                  {formatMessage({
                    id: `assessments.${levelNamesType ?? AssessmentLevelNamesType.Intervals}WithPercentageSpan.${intervals}.level${index + 1}`,
                  })}
                </Typography>
                <Stack spacing={2}>
                  <ControlledTextFieldWithFormatting
                    name={`level${index + 1}Title`}
                    control={control}
                    defaultValue={assessmentTemplate?.[`level${index + 1}Title` as keyof AssessmentTemplate] as string}
                    label={formatMessage({ id: 'assessments.shortDescription' })}
                  />
                  <ControlledTextFieldWithFormatting
                    multiline
                    name={`level${index + 1}Description`}
                    control={control}
                    defaultValue={
                      assessmentTemplate?.[`level${index + 1}Description` as keyof AssessmentTemplate] as string
                    }
                    label={formatMessage({ id: 'general.description' })}
                  />
                </Stack>
              </Stack>
            ))}
        </Stack>

        {assessmentType === AssessmentType.Flexible && (
          <>
            <Stack spacing={2} px={4}>
              <Typography variant="h5">{formatMessage({ id: 'assessments.sections' })}</Typography>
              <Typography variant="body2">{formatMessage({ id: 'assessments.sectionsDescription' })}</Typography>
            </Stack>
            {sections?.map((s, i) => {
              const sectionFieldName = `section${i + 1}Name`
              const sectionError = (errors as Record<string, FieldError>)[sectionFieldName]

              return (
                <Stack
                  spacing={4}
                  key={s.id}
                  sx={{ borderRadius: 2, borderStyle: 'solid', borderWidth: 1, borderColor: 'grey.400', p: 4 }}
                >
                  <Stack spacing={1}>
                    <Typography variant="subtitle1">
                      {formatMessage({ id: 'assessments.section' }, { number: i + 1 })}
                    </Typography>
                    <Typography variant="caption">{formatMessage({ id: 'assessments.classificationInfo' })}</Typography>
                  </Stack>
                  <TextField
                    name={sectionFieldName}
                    inputRef={register({ required: formatMessage({ id: 'form.validation.required' }) })}
                    label={formatMessage({ id: 'assessments.sectionName' })}
                    defaultValue={s.name}
                    fullWidth
                    error={!!sectionError}
                    helperText={sectionError?.message}
                  />
                  {s.questions?.map((q, qi) => (
                    <Paper key={q.questionId} elevation={0} sx={{ p: 2 }}>
                      <Stack direction="row" justifyContent="space-between" py={1} px={1}>
                        <IconButton
                          onClick={() =>
                            openDrawer(<DrawerViewAccessorQuestionGuidance question={q.question} />, {
                              anchor: 'left',
                            })
                          }
                        >
                          <InfoOutlined />
                        </IconButton>
                        {Boolean(q.question.questionAnswerClassificationRules?.length) && (
                          <Box>
                            <IconButton onClick={() => openQuestionClassificationDialog(q)}>
                              <Edit />
                            </IconButton>
                          </Box>
                        )}
                      </Stack>
                      <Typography variant="subtitle1" pt={2} pb={2} pl={1}>
                        {i + 1}.{qi + 1} {q.question.kpiName || q.question.questionText}
                      </Typography>
                      {!q.question.questionAnswerClassificationRules?.length ? (
                        <Box p={3}>
                          <EmptyState
                            title={formatMessage({ id: 'assessments.noClassification' })}
                            iconComponent={ListAltOutlined}
                            variant={EmptyStateVariant.Small}
                          >
                            <Button
                              startIcon={<Add />}
                              variant="contained"
                              onClick={() => openQuestionClassificationDialog(q)}
                            >
                              {formatMessage({ id: 'assessments.addClassification' })}
                            </Button>
                          </EmptyState>
                        </Box>
                      ) : (
                        <AnswerClassificationPreviewCard
                          showTitle={false}
                          questionAnswerClassifications={q.question.questionAnswerClassificationRules}
                          points={getFormPointsForQuestion(q.questionId, i)}
                        />
                      )}
                    </Paper>
                  ))}
                </Stack>
              )
            })}
          </>
        )}

        {isBaseline && (
          <>
            <Stack spacing={2} px={4}>
              <Typography variant="h5">{formatMessage({ id: 'assessments.sections' })}</Typography>
              <Typography variant="body2">{formatMessage({ id: 'assessments.sectionsDescription' })}</Typography>
            </Stack>
            {sections?.map((s, i) => {
              return (
                <Stack
                  spacing={4}
                  key={s.id}
                  sx={{ borderRadius: 2, borderStyle: 'solid', borderWidth: 1, borderColor: 'grey.400', p: 4 }}
                >
                  <Stack spacing={1}>
                    <Typography variant="subtitle1">{s.name}</Typography>
                  </Stack>
                  {s.questions?.map((q, qi) => (
                    <Paper key={q.questionId} elevation={0} sx={{ p: 2 }}>
                      <Stack direction="row" py={1} px={1} alignItems="start">
                        <IconButton
                          sx={{}}
                          onClick={() =>
                            openDrawer(<DrawerViewAccessorQuestionGuidance question={q.question} />, {
                              anchor: 'left',
                            })
                          }
                        >
                          <InfoOutlined />
                        </IconButton>
                        <Typography variant="subtitle1" pt={1} pb={2} pl={1} sx={{ display: 'flex', gap: 1.5 }}>
                          <Box>
                            {i + 1}.{qi + 1}
                          </Box>
                          <Box>{q.question.questionText}</Box>
                        </Typography>
                        <Box ml="auto" pl={4} pt={1} pr={2}>
                          <Tooltip title={formatMessage({ id: 'templateBuilder.questionId' })} arrow>
                            <Box display="flex">
                              <Typography variant="subtitle1">{formatMessage({ id: 'general.id' })}</Typography>
                              <Box pl={1}>{q.question.id}</Box>
                            </Box>
                          </Tooltip>
                        </Box>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )
            })}
          </>
        )}

        {assessmentType === AssessmentType.StandardAssessment && (
          <Stack
            spacing={4}
            sx={{ borderRadius: 2, borderStyle: 'solid', borderWidth: 1, borderColor: 'grey.400', p: 4 }}
          >
            <Box>
              <Typography variant="subtitle1">
                {formatMessage({ id: 'templateBuilder.answerClassifications.classification' })}
              </Typography>
              <Typography variant="caption">{formatMessage({ id: 'assessments.classificationInfo' })}</Typography>
            </Box>
            {sections
              ?.flatMap(s => s.questions)
              ?.map(q => (
                <Paper key={q.questionId} elevation={0} sx={{ p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" py={1} px={1}>
                    <IconButton
                      onClick={() =>
                        openDrawer(<DrawerViewAccessorQuestionGuidance question={q.question} />, {
                          anchor: 'left',
                        })
                      }
                    >
                      <InfoOutlined />
                    </IconButton>
                    {Boolean(q.question.questionAnswerClassificationRules?.length) && (
                      <Box>
                        <IconButton onClick={() => openQuestionClassificationDialog(q)}>
                          <Edit />
                        </IconButton>
                      </Box>
                    )}
                  </Stack>
                  <Typography variant="subtitle1" pt={2} pb={2} pl={1}>
                    {q.question.kpiName || q.question.questionText}
                  </Typography>
                  {!q.question.questionAnswerClassificationRules?.length ? (
                    <Box p={3}>
                      <EmptyState
                        title={formatMessage({ id: 'assessments.noClassification' })}
                        iconComponent={ListAltOutlined}
                        variant={EmptyStateVariant.Small}
                      >
                        <Button
                          startIcon={<Add />}
                          variant="contained"
                          onClick={() => openQuestionClassificationDialog(q)}
                        >
                          {formatMessage({ id: 'assessments.addClassification' })}
                        </Button>
                      </EmptyState>
                    </Box>
                  ) : (
                    <AnswerClassificationPreviewCard
                      showTitle={false}
                      questionAnswerClassifications={q.question.questionAnswerClassificationRules}
                      points={getFormPointsForQuestion(q.questionId)}
                    />
                  )}
                </Paper>
              ))}
          </Stack>
        )}
      </Stack>

      <EditQuestionClassificationDialog
        isOpen={isOpen}
        closeDialog={closeDialog}
        setCurrentQuestion={setCurrentQuestion}
        currentQuestion={currentQuestion}
        assessmentType={assessmentType}
        onClassificationSave={assessmentType === AssessmentType.Flexible ? handleClassificationSave : undefined}
      />
    </Stack>
  )
}

export default AssessmentForm
